import { Injectable } from '@angular/core';
import { QuestionsService } from '../survey/questions.service';
import { VariablesService } from '../survey/variables.service';
import { SlackMessagesApiService, HelpdeskApiService, SnackbarService } from '@tangilla/tg-ng-core';
import { SurveyService } from '../survey/survey.service';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ErrorsService {
  showErrorScreen?: boolean;
  errorMessage?: string;
  errorTitle?: string;

  constructor(private questionsService: QuestionsService,
              private variablesService: VariablesService,
              private surveyService: SurveyService,
              private slackMessagesApiService: SlackMessagesApiService,
              private helpdeskService: HelpdeskApiService,
              private snackbarService: SnackbarService,
  ) {
  }

  processError(error?: any, errorType?: 'soft' | 'hard', title?: string, message?: string) {
    let errorCode;
    let errorMessage;
    if (error && error.error && error.error.error) {
      errorCode = error.error.error.status;
      errorMessage = error.error.error.message;
    }

    const actualMessage = message ? message : errorMessage;

    let exitFormTitle = '';
    let exitFormMessage = '';

    if (errorCode === 401) {
      exitFormTitle = title ? title : 'The application has timed out.';
      exitFormMessage = `<p>Applications must be completed within 2 hours of being started. This application has timed out.</p><p>Please start the application again.</p>`;
    } else if (errorType === 'soft') {
      return;
    } else if (errorType !== 'hard' && errorCode && [404, 409].includes(errorCode)) {
      this.snackbarService.error(actualMessage);
      return;
    } else {
      exitFormTitle = title ? title : '<h4>We\'re sorry, something has gone wrong.</h4>';
      exitFormMessage = `<p>Our team has been alerted, but right now we are unable to continue.</p><p>Please try again shortly.</p>`;
    }

    this.logErrorToSlack(error);
    this.exitForm(exitFormTitle, exitFormMessage);
  }

  logErrorToSlack(error?: any, title?: string) {
    this.clearSignQuestion();

    const surveyDataObject = this.surveyService.surveyModel ? this.surveyService.surveyModel.data : undefined;
    const surveyDataString = this.surveyService.surveyModel ? JSON.stringify(this.surveyService.surveyModel.data) : undefined;
    const userName = surveyDataObject && surveyDataObject.FirstName ?
      `${surveyDataObject.FirstName} ${surveyDataObject.LastName} (${surveyDataObject.emailaddress})` :
      'a user';

    const baseMessage = `${userName} was completing ${this.surveyService.formSource} (rhid: ${this.surveyService.responseHeaderId}, env: ${environment.environmentName})`;
    const titleMessage = title ? `${title} \n\n ` : '';
    const extraMessage = this.getSlackExtraMessage(error, surveyDataString);
    const message = `${titleMessage}${baseMessage}${extraMessage}`;

    this.slackMessagesApiService.postSlackMessage({message, message_type: 'error'})
      .subscribe({
        next: data => {
        },
        error: error => {
          console.error('Error logging to Slack:', error);
        }
      });
  }

  getSlackExtraMessage(error: any, surveyDataString?: string) {
    let errorMessage = '';
    if (error) {
      errorMessage = ` (${error.error.error.status} - ${error.error.error.message})`;
    }
    const errorRelatedMessage = `and experienced an API error${errorMessage ? errorMessage : ''}.`;

    const browserData = JSON.stringify(window.navigator.userAgent);

    const ccoRecordMessage = surveyDataString ? ` CCO Record created: ${this.variablesService.ccoRecordCreated}` : ''; // TODO: Change;
    const memberIdMessage = surveyDataString && this.questionsService.nrds?.value ? ` - Member ID: ${this.questionsService.nrds?.value}.` : '';
    const browserDataMessage = `\n\n Browser Data : ${browserData}`;
    const endpointUrlMessage = error && error.error ? `\n\n Request path : ${error.error.url}` : '';
    const xhrDump = error ? `\n\n XHRDump : ${JSON.stringify(error)}` : '';
    const loggedPayloadMessage = surveyDataString ? `\n\n Logged Payload : ${surveyDataString}` : '';

    return `${errorRelatedMessage}${ccoRecordMessage}${memberIdMessage}${browserDataMessage}${endpointUrlMessage}${xhrDump}${loggedPayloadMessage}`;
  }

  exitForm(title: string, message: string) {
    this.errorTitle = title;
    this.errorMessage = message;
    this.showErrorScreen = true;
  }

  clearSignQuestion() {
    const signQuestion = this.surveyService.surveyModel?.getQuestionByValueName('sign');
    if (!!signQuestion) {
      signQuestion.clearValue();
    }
  }

  logToSlackSupportEscalations(title: string, data: any, form_source: string) {
    data = JSON.stringify(data);
    // ** log a payload to slack
    const url = 'https://hooks.slack.com/services/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';
    const applicname = (('undefined' !== this.surveyService.surveyModel.getValue('FirstName')) ? this.surveyService.surveyModel.getValue('FirstName') + ' ' + this.surveyService.surveyModel.getValue('LastName') + ' (' + this.surveyService.surveyModel.getValue('emailaddress') + ')' : 'a user');
    const text = `${title} \n\n ${applicname} was completing ${form_source} (rhid: ${this.surveyService.responseHeaderId}) : \n\n Logged Payload : \`${data}\``;
    // Send post request with body { text: text } to url

    return new Promise((resolve, reject) => {
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({text: text}),
      })
        .then(response => {
          if (!response.ok) {
            console.error('Failed to log to Slack:', response.statusText);
            reject(response);
          } else {
            resolve(response);
          }
        })
        .catch(error => {
          console.error('Error logging to Slack:', error);
          resolve(error);
        });
    });
  }

  postHelpdeskTicketToAssociation(subject: string, message: string) {
    const body = {
      first_name: this.surveyService.surveyModel.getValue('FirstName'),
      last_name: this.surveyService.surveyModel.getValue('LastName'),
      email: this.surveyService.surveyModel.getValue('emailaddress'),
      subject,
      message
    };
    this.helpdeskService.submitFormTicket(body).subscribe();
  }
}
